import React, { useMemo, useRef, useState } from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';

import styles from './physicalCards.module.scss';
import { actions } from '../../state';
import { Button, Spinner } from '../../components';

const ImportModal = ({
  dispatch,
  templateDownloading,
  importValidating,
  importCommitting,
  importBatch,
  importCommitResult,
}) => {
  const [step, setStep] = useState(1);
  const [selectedFile, setSelectedFile] = useState(null);
  const fileInputRef = useRef(null);

  const errorRows = useMemo(() => {
    if (!importBatch || !Array.isArray(importBatch.row_results)) {
      return [];
    }

    return importBatch.row_results.filter(row => row.status === 'error');
  }, [importBatch]);

  const skippedRowsCount = useMemo(() => {
    if (!importBatch || !Array.isArray(importBatch.row_results)) {
      return 0;
    }

    return importBatch.row_results.filter(row => row.status === 'skip').length;
  }, [importBatch]);

  const okRows = importBatch ? importBatch.ok_rows || 0 : 0;
  const errorRowsCount = importBatch ? importBatch.error_rows || 0 : 0;
  const totalRows = importBatch ? importBatch.total_rows || 0 : 0;
  const canCommit = !importValidating && okRows > 0;

  const handleDownloadTemplate = () => {
    dispatch(actions.physicalCards.downloadPhysicalCardsImportTemplateAction());
  };

  const handleClearImport = () => {
    setSelectedFile(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
    dispatch(actions.physicalCards.clearPhysicalCardsImportAction());
  };

  const handleNext = () => {
    setStep(2);
  };

  const handleBack = () => {
    handleClearImport();
    setStep(1);
  };

  const handleOpenFilePicker = () => {
    if (importValidating || importCommitting) {
      return;
    }
    fileInputRef.current.click();
  };

  const handleFileChange = event => {
    const file = event.target.files && event.target.files[0];
    if (!file) {
      return;
    }

    setSelectedFile(file);
    dispatch(actions.physicalCards.setPhysicalCardsImportBatchAction(null));
    dispatch(
      actions.physicalCards.setPhysicalCardsImportCommitResultAction(null)
    );
    dispatch(actions.physicalCards.validatePhysicalCardsImportAction(file));
    event.target.value = '';
  };

  const handleDownloadFailedRows = () => {
    if (!importBatch || !importBatch.id) {
      return;
    }

    dispatch(
      actions.physicalCards.downloadPhysicalCardsImportFailedRowsAction(
        importBatch.id
      )
    );
  };

  const handleCommit = () => {
    if (!canCommit || !importBatch || !importBatch.id) {
      return;
    }

    setStep(3);
    dispatch(
      actions.physicalCards.commitPhysicalCardsImportAction(importBatch.id)
    );
  };

  const handleClose = () => {
    dispatch(actions.physicalCards.clearPhysicalCardsImportAction());
    dispatch(actions.modals.clearModals());
  };

  const getErrorReason = row => {
    const firstError =
      Array.isArray(row.errors) && row.errors.length ? row.errors[0] : null;
    return firstError ? firstError.message : 'Nežinoma klaida';
  };

  const renderStep1 = () => (
    <>
      <p className={styles.importModalDescription}>
        Atsisiųskite CSV šabloną, užpildykite jį pagal stulpelių antraštes ir
        kitame žingsnyje įkelkite failą patikrinimui.
      </p>

      <button
        type="button"
        className={styles.importTemplateLink}
        onClick={handleDownloadTemplate}
        disabled={templateDownloading}
      >
        {templateDownloading ? 'Atsisiunčiama…' : 'Atsisiųsti CSV šabloną'}
      </button>

      <div className={styles.importModalActions}>
        <Button color="primary" type="button" onClick={handleNext}>
          Toliau
        </Button>
      </div>
    </>
  );

  const renderStep2 = () => (
    <>
      <p className={styles.importModalDescription}>
        Pasirinkite užpildytą CSV failą. Sistema patikrins eilutes prieš
        importą.
      </p>

      <input
        ref={fileInputRef}
        className={styles.importFileInput}
        type="file"
        accept=".csv,text/csv"
        onChange={handleFileChange}
        disabled={importValidating}
      />

      <button
        type="button"
        className={styles.importFileButton}
        onClick={handleOpenFilePicker}
        disabled={importValidating}
      >
        Pasirinkti CSV failą
      </button>

      {selectedFile && (
        <p className={styles.importSelectedFile}>
          Pasirinkta:{' '}
          {importBatch && importBatch.filename
            ? importBatch.filename
            : selectedFile.name}
        </p>
      )}

      {importValidating && (
        <div className={styles.importProgress}>
          <Spinner />
          <span>Tikrinama…</span>
        </div>
      )}

      {!importValidating && importBatch && (
        <div className={styles.importPreview}>
          <ul className={styles.importStats}>
            <li>
              Failas:{' '}
              {importBatch.filename || (selectedFile && selectedFile.name)}
            </li>
            <li>Viso eilučių: {totalRows}</li>
            <li>Tinka importui: {okRows}</li>
            <li>Su klaidomis: {errorRowsCount}</li>
          </ul>

          {errorRowsCount > 0 && (
            <>
              <button
                type="button"
                className={styles.importTemplateLink}
                onClick={handleDownloadFailedRows}
              >
                Atsisiųsti klaidingas eilutes
              </button>

              <div className={styles.importErrorList}>
                {errorRows.map(row => (
                  <div key={row.row_number} className={styles.importErrorItem}>
                    <strong>Eilutė {row.row_number}:</strong>{' '}
                    {getErrorReason(row)}
                  </div>
                ))}
              </div>
            </>
          )}
        </div>
      )}

      <div className={styles.importModalActions}>
        <Button
          color="dim"
          type="button"
          onClick={handleBack}
          disabled={importValidating}
        >
          Atgal
        </Button>
        <Button
          color={canCommit ? 'primary' : 'outline'}
          type="button"
          onClick={handleCommit}
          disabled={!canCommit}
        >
          Patvirtinti
        </Button>
      </div>
    </>
  );

  const renderStep3 = () => {
    if (importCommitting || !importCommitResult) {
      return (
        <>
          <div className={styles.importProgress}>
            <Spinner />
            <span>Importuojama…</span>
          </div>
        </>
      );
    }

    if (!importCommitResult.success) {
      return (
        <>
          <p className={styles.importModalDescription}>
            {importCommitResult.message || 'Importas nepavyko.'}
          </p>
          <div className={styles.importModalActions}>
            <Button color="light" type="button" onClick={handleClose}>
              Uždaryti
            </Button>
          </div>
        </>
      );
    }

    return (
      <>
        <p className={styles.importModalDescription}>Importas baigtas.</p>
        <ul className={styles.importStats}>
          <li>Importuota: {importCommitResult.imported || 0}</li>
          <li>Praleista: {skippedRowsCount}</li>
          <li>Nepavyko: {errorRowsCount}</li>
        </ul>
        <div className={styles.importModalActions}>
          <Button color="light" type="button" onClick={handleClose}>
            Uždaryti
          </Button>
        </div>
      </>
    );
  };

  return (
    <div className={styles.importModal}>
      {step === 1 && renderStep1()}
      {step === 2 && renderStep2()}
      {step === 3 && renderStep3()}
    </div>
  );
};

const mapStateToProps = state => ({
  templateDownloading: state.physicalCards.templateDownloading,
  importValidating: state.physicalCards.importValidating,
  importCommitting: state.physicalCards.importCommitting,
  importBatch: state.physicalCards.importBatch,
  importCommitResult: state.physicalCards.importCommitResult,
});

ImportModal.propTypes = {
  dispatch: PropTypes.func.isRequired,
  templateDownloading: PropTypes.bool,
  importValidating: PropTypes.bool,
  importCommitting: PropTypes.bool,
  importBatch: PropTypes.shape({
    id: PropTypes.number,
    total_rows: PropTypes.number,
    ok_rows: PropTypes.number,
    error_rows: PropTypes.number,
    row_results: PropTypes.array,
  }),
  importCommitResult: PropTypes.shape({
    success: PropTypes.bool,
    imported: PropTypes.number,
    message: PropTypes.string,
  }),
};

ImportModal.defaultProps = {
  templateDownloading: false,
  importValidating: false,
  importCommitting: false,
  importBatch: null,
  importCommitResult: null,
};

export default connect(mapStateToProps)(ImportModal);
